import { YardPlaceSceneBlock, YardPlaceSceneData } from '../design-system';
import { Block, ContainerGetOneQuery } from '../types';

const getValidBlocks = (blocks: Block[] = []) =>
  blocks.filter((item) => !!item.block && !!item.rowLength && !!item.slotLength);

export const blocksToSlotNames = (blocks: Block[] = []) => {
  const blocksData = getValidBlocks(blocks).map((item) => ({
    block: item.block,
    rows: Array.from({ length: Number(item.rowLength) }, (_, i) => i + 1),
    slots: Array.from({ length: Number(item.slotLength) }, (_, i) => i + 1),
  }));

  const result: string[] = [];

  for (const block of blocksData) {
    if (!block.slots.length) continue;

    for (const row of block.rows) {
      for (const slot of block.slots) {
        result.push(`${block.block}(${row},${slot})`);
      }
    }
  }

  return result;
};

const parseBlocksScene = (blocksScene: string): YardPlaceSceneData =>
  JSON.parse(blocksScene) as YardPlaceSceneData;

const stringifyBlocksScene = (data: YardPlaceSceneData) =>
  JSON.stringify({
    blocks: data.blocks ?? [],
    containers: data.containers ?? [],
    items: data.items ?? [],
  });

const blocksToSceneBlocks = (blocks: Block[] = []): YardPlaceSceneBlock[] => {
  let nextColumn = 0;

  return getValidBlocks(blocks).map((item) => {
    const rowsLength = Number(item.rowLength);
    const slotsLength = Number(item.slotLength);
    const sceneBlock: YardPlaceSceneBlock = {
      name: item.block,
      rowsLength,
      slotsLength,
      rotate: 0,
      position: { x: nextColumn, y: 0 },
    };

    nextColumn += Math.max(rowsLength, slotsLength) + 2;

    return sceneBlock;
  });
};

const createBlocksSceneFromBlocks = (blocks: Block[] = []) =>
  stringifyBlocksScene({
    blocks: blocksToSceneBlocks(blocks),
    containers: [],
    items: [],
  });

const findSceneBlockByName = (sceneBlocks: YardPlaceSceneBlock[], blockName: string) =>
  sceneBlocks.find((sceneBlock) => sceneBlock.name.toUpperCase() === blockName.toUpperCase());

const areBlocksEqual = (sceneBlocks: YardPlaceSceneBlock[] = [], blocks: Block[] = []) => {
  const validBlocks = getValidBlocks(blocks);

  if (sceneBlocks.length !== validBlocks.length) return false;

  return validBlocks.every((block) => {
    const sceneBlock = findSceneBlockByName(sceneBlocks, block.block);

    return (
      !!sceneBlock &&
      sceneBlock.rowsLength === Number(block.rowLength) &&
      sceneBlock.slotsLength === Number(block.slotLength)
    );
  });
};

const syncSceneBlocksWithBlocks = (
  sceneBlocks: YardPlaceSceneBlock[] = [],
  blocks: Block[] = [],
): YardPlaceSceneBlock[] => {
  const validBlocks = getValidBlocks(blocks);
  let nextColumn = 0;

  return validBlocks.map((item) => {
    const rowsLength = Number(item.rowLength);
    const slotsLength = Number(item.slotLength);
    const existingBlock = findSceneBlockByName(sceneBlocks, item.block);

    if (existingBlock) {
      const position = existingBlock.position ?? { x: nextColumn, y: 0 };
      nextColumn = Math.max(nextColumn, position.x + Math.max(rowsLength, slotsLength) + 2);

      return {
        ...existingBlock,
        name: item.block,
        rowsLength,
        slotsLength,
      };
    }

    const sceneBlock: YardPlaceSceneBlock = {
      name: item.block,
      rowsLength,
      slotsLength,
      rotate: 0,
      position: { x: nextColumn, y: 0 },
    };

    nextColumn += Math.max(rowsLength, slotsLength) + 2;

    return sceneBlock;
  });
};

export const filterBlocksScene = (blocksScene: string, blocks: Block[] = []) => {
  const validBlocks = getValidBlocks(blocks);

  if (!blocksScene) {
    return createBlocksSceneFromBlocks(validBlocks);
  }

  const parsed = parseBlocksScene(blocksScene);
  const sceneBlocks = parsed.blocks ?? [];

  if (!sceneBlocks.length) {
    return createBlocksSceneFromBlocks(validBlocks);
  }

  if (!areBlocksEqual(sceneBlocks, validBlocks)) {
    return stringifyBlocksScene({
      blocks: syncSceneBlocksWithBlocks(sceneBlocks, validBlocks),
      containers: [],
      items: parsed.items ?? [],
    });
  }

  return stringifyBlocksScene(parsed);
};

export const checkBlocksScene = (blocksScene: string, blocks: Block[]) => {
  if (!blocksScene) return true;

  const sceneBlocks = parseBlocksScene(blocksScene).blocks ?? [];

  if (!sceneBlocks.length) {
    return !getValidBlocks(blocks).length;
  }

  return areBlocksEqual(sceneBlocks, blocks);
};

export const checkContainerPlace = (container: ContainerGetOneQuery) => {
  if (!container.block || !container.row || !container.slot) return false;

  return true;
};
